/**
 * ComposerBar.jsx — the selection tray and the add-slide controls. Sits at the
 * foot of the library, or at the top of the carousel view.
 *
 * At phone width the five style buttons collapse into one select and the bulk
 * row hides behind a "More ways" disclosure, so the bar stays two rows tall
 * above the tab bar. Desktop keeps both rows open.
 */

import { useState } from 'react';
import {
  addOne, addSingles, clearSel, deselectAt, moveSelAt, openPeek, pairAdd, selectImgById,
  setAddStyle, setPanoN, swapSel, useCarousel,
} from '../../hooks/useCarouselStore';
import { useMobile } from '../../hooks/useMobile';
import { addHintText, roleFor } from '../../lib/carousel/library.js';
import LayoutGallery from './LayoutGallery';
import { revealTile } from './dom.js';

const STYLES = [
  ['single', 'Single'],
  ['stack', 'Stacked'],
  ['row', 'Side by side'],
  ['hero3', 'Hero + 3'],
  ['pano', 'Panorama'],
];

function TrayChip({ im, i, n, addStyle }) {
  const role = roleFor(addStyle, i);
  return (
    <div className={'tray-chip' + (addStyle === 'pano' && i > 0 ? ' extra' : '')} title={im.name}>
      <img src={im.thumb} alt="" onClick={() => revealTile(im.id)} />
      <span className="role">{(i + 1) + (role ? ' ' + role : '')}</span>
      <div className="acts">
        <button type="button" title="Move earlier" aria-label="Move earlier" onClick={() => { if (i > 0) moveSelAt(i, -1); }}>◀</button>
        <button type="button" title="Move later" aria-label="Move later" onClick={() => { if (i < n - 1) moveSelAt(i, 1); }}>▶</button>
        <button type="button" title="Deselect" aria-label="Deselect" onClick={() => deselectAt(i)}>✕</button>
      </div>
    </div>
  );
}

export default function ComposerBar() {
  const { isMobile } = useMobile();
  const sel = useCarousel((s) => s.sel);
  const addStyle = useCarousel((s) => s.addStyle);
  const panoN = useCarousel((s) => s.panoN);
  const libN = useCarousel((s) => s.library.length);
  const imgById = useCarousel(selectImgById);
  const [layoutsOpen, setLayoutsOpen] = useState(false);
  const [more, setMore] = useState(false);

  const hint = addHintText(addStyle, sel.map(imgById), panoN, libN);

  const tray = sel.length > 0 && (
    <div className="tray">
      {sel.map((id, i) => {
        const im = imgById(id);
        return im ? <TrayChip key={id} im={im} i={i} n={sel.length} addStyle={addStyle} /> : null;
      })}
    </div>
  );

  const panoCount = addStyle === 'pano' && (
    <span className="ctl">
      <span className="lbl">across</span>
      <input type="number" min="2" max="6" value={panoN} onChange={(e) => setPanoN(e.target.value)} aria-label="Panorama slides" />
      <span className="lbl">slides</span>
    </span>
  );

  const bulk = (
    <>
      <span className="lbl">All selected</span>
      <button type="button" className="btn tiny" onClick={addSingles}>Add as singles</button>
      <button type="button" className="btn tiny" onClick={() => pairAdd('stack')}>Pair · stacked</button>
      <button type="button" className="btn tiny" onClick={() => pairAdd('row')}>Pair · side by side</button>
      <button type="button" className="btn tiny" onClick={() => pairAdd('hero3')}>Fours · hero + 3</button>
      <button type="button" className="btn tiny ghost" onClick={clearSel}>Clear</button>
    </>
  );

  const gallery = <LayoutGallery open={layoutsOpen} onClose={() => setLayoutsOpen(false)} />;

  if (isMobile) {
    return (
      <div className="composer cc-mcomposer">
        {tray}
        <div className="comp-row cc-mcomp">
          <select
            className="cc-mstyle"
            value={addStyle}
            aria-label="Slide style"
            onChange={(e) => setAddStyle(e.target.value)}
          >
            {STYLES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
          {panoCount}
          <button type="button" className="btn primary" onClick={addOne}>Add</button>
          <button
            type="button"
            className={'btn tiny ghost cc-mmenu' + (more ? ' on' : '')}
            aria-expanded={more}
            aria-label="More ways to add"
            onClick={() => setMore(!more)}
          >
            ⋯
          </button>
        </div>
        {more && (
          <>
            <div className="comp-row cc-mbulk">{bulk}</div>
            <div className="comp-row cc-mbulk">
              <button type="button" className="btn tiny" onClick={() => { setMore(false); setLayoutsOpen(true); }}>Layouts…</button>
              {sel.length === 2 && <button type="button" className="btn tiny ghost" onClick={swapSel}>⇅ Swap</button>}
              <button type="button" className="btn tiny ghost" disabled={sel.length < 2} onClick={() => openPeek(sel.slice(0, 2))}>Compare 2</button>
            </div>
          </>
        )}
        <span className="hint cc-mhint">{hint}</span>
        {gallery}
      </div>
    );
  }

  return (
    <div className="composer">
      {tray}
      <div className="comp-row">
        <span className="lbl">Add one slide</span>
        <div className="seg">
          {STYLES.map(([k, label]) => (
            <button key={k} type="button" aria-pressed={addStyle === k} onClick={() => setAddStyle(k)}>{label}</button>
          ))}
        </div>
        {panoCount}
        <button type="button" className="btn" onClick={addOne}>Add to carousel</button>
        <button type="button" className="btn tiny" onClick={() => setLayoutsOpen(true)}>Layouts…</button>
        {sel.length === 2 && <button type="button" className="btn tiny ghost" onClick={swapSel}>⇅ Swap</button>}
        <button type="button" className="btn tiny ghost" disabled={sel.length < 2} onClick={() => openPeek(sel.slice(0, 2))}>Compare 2</button>
        <span className="hint">{hint}</span>
      </div>
      <div className="comp-row">{bulk}</div>
      {gallery}
    </div>
  );
}
